import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { createSelector, select, Store } from '@ngrx/store';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { ORDER_EXPERIENCES } from '@ordersnproducts/modules/shared/constants/order-experiences';
import { UnloadTypePumpCodesFrance } from '@ordersnproducts/modules/shared/models/pump-type.enum';
import { ExperiencesService } from '@ordersnproducts/modules/shared/services/experiences.service';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, debounceTime, filter, map, startWith, switchMap } from 'rxjs/operators';
import { SelectOptionI } from '../../../../../../shared/modules/configurable-form/components/form-select/models/select-option-i';
import { SelectOptionLoaderI } from '../../../../../../shared/modules/configurable-form/components/form-select/models/select-option-loader-i';
import { getActiveExperienceCode, getPumpCapacities, OrderBuilderState } from '../../../../../reducers';
import {
  getOpenPlatformProductAddedValues,
  getVAPAndFilteredUnloadTypes,
} from '../../../selectors/added-value.selectors';
import {
  getRmsRecipeSpecifications,
  getRmsSpecificationsIsModify,
  getRmsUpdatespecifications,
} from '../../../selectors';
import { getAvailableProducts, getOpenPlatformProducts } from '../../../selectors/products.selectors';
import {
  getApplications,
  getLoadSizes,
  getSlumps,
  getSlumpsProduct,
  getSpacings,
  getFilterSpacings,
  getUnloadTimes,
  getUnloadTypes,
  getSlumpsAndProducts,
  getFilteredPumpabilities,
  getFilteredUnloadTimes,
  getFullAndFilterUnloadTypes,
  getLocalSpacings,
  getUnloadTimesWithLocalData,
} from '../../../selectors/single-catalogs';
import { getLoadSizeValue } from '@ordersnproducts/utils/common-utils';
import { FieldConfig } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/field-config.interface';
import { NA_SPACING_CODE } from '@ordersnproducts/modules/order-builder/modules/specification/configs/us/constants';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';
import { getDcoStatusState } from '../../../../../../shared/reducers';
import { Router } from '@angular/router';
import { ImSpacing } from '@cemex-core/cmx-api';
import { getOrderSelectedDetail } from '@ordersnproducts/modules/new-modify-order/selectors/order-selected-detail.selector';
import { ImprovementsModifyProvider } from '@ordersnproducts/modules/new-modify-order/modules/rms-modify-product-specification/providers/improvements-modify.provider';
import isAfterCutOffTimeUK = ImprovementsModifyProvider.isAfterCutOffTimeUK;
import {
  UNLOAD_TIMES_TO_UNLOAD_TYPE_MAP,
  UNLOAD_TYPE_MIN_TIMES_MAP,
} from '@ordersnproducts/modules/order-builder/modules/specification/constants/unload-type-min-times-map';

export const getData = createSelector(
  getOpenPlatformProducts,
  getSlumpsProduct,
  getActiveExperienceCode,
  getRmsRecipeSpecifications,
  (stateProducts, stateSlumps, experienceCode, recipes) => {
    return {
      stateProducts,
      stateSlumps,
      experienceCode,
      recipes,
    };
  },
);
export const getDataForm = createSelector(
  getRmsUpdatespecifications,
  getOpenPlatformProducts,
  getSlumpsProduct,
  getActiveExperienceCode,
  (updateForm, stateProducts, stateSlumps, experienceCode) => {
    return {
      updateForm,
      stateProducts,
      stateSlumps,
      experienceCode,
    };
  },
);

export const getRepeatData = createSelector(
  getOpenPlatformProducts,
  getSlumpsProduct,
  getActiveExperienceCode,
  getRmsRecipeSpecifications,
  (stateProducts, stateSlumps, experienceCode, recipes) => {
    return {
      stateProducts,
      stateSlumps,
      experienceCode,
      recipes,
    };
  },
);

export const getDataModify = createSelector(
  getOpenPlatformProducts,
  getSlumpsProduct,
  getActiveExperienceCode,
  getRmsRecipeSpecifications,
  (stateProducts, stateSlumps, experienceCode, recipes) => {
    return {
      stateProducts,
      stateSlumps,
      experienceCode,
      recipes,
    };
  },
);

export const getDataLegacy = createSelector(
  getAvailableProducts,
  getSlumpsProduct,
  getActiveExperienceCode,
  getRmsRecipeSpecifications,
  (stateProducts, stateSlumps, experienceCode, recipes) => {
    return {
      stateProducts,
      stateSlumps,
      experienceCode,
      recipes,
    };
  },
);

@Injectable()
export class SingleApplicationsLoaderService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getApplications),
      filter((state) => !!state),
      map((applications) => {
        if (applications.length) {
          return applications.map((application) => {
            return {
              id: application.applicationCode,
              name: `${application.applicationDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}

@Injectable()
export class SingleSpacingsLoaderService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getSpacings),
      filter((state) => !!state),
      map((spacings) => {
        if (spacings.length) {
          return spacings.map((spacing) => {
            return {
              id: spacing.spacingCode,
              name: `${spacing.spacingDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}
@Injectable()
export class SingleSpacingsLoaderUkService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getFilterSpacings),
      map((spacings) => {
        if (spacings.length) {
          return spacings.map((spacing) => {
            return {
              id: spacing.spacingCode,
              name: `${spacing.spacingDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}
@Injectable()
export class SingleLocalSpacingsLoaderUkService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getFilterSpacings),
      map((spacings) => {
        const spacingFilter = spacings.filter(
          (spacing, index, self) => index === self.findIndex((o) => o.spacingId === spacing.spacingId),
        );
        if (spacingFilter.length) {
          // const sorted = spacingFilter.sort((a, b) => a['spacingMinutes'] - b['spacingMinutes']); Uncomment to sort by minutes
          return spacingFilter.map((spacing) => {
            return {
              id: spacing.spacingCode,
              name: `${spacing.spacingDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}

@Injectable()
export class SingleLocalSpacingsLoaderMXService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getFilterSpacings),
      map((spacings) => {
        const spacingFilter = spacings.filter(
          (spacing, index, self) => index === self.findIndex((o) => o.spacingId === spacing.spacingId),
        );
        if (spacingFilter.length) {
          // Uncomment to sort by minutes if needed
          // const sorted = spacingFilter.sort((a, b) => a['spacingMinutes'] - b['spacingMinutes']);
          return spacingFilter.map((spacing) => {
            return {
              id: spacing.spacingCode,
              name: `${spacing.spacingDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}

/**
 * Uses list of spacing catalog + local one added from COMMAND order
 * Uses spacingId instead of spacingCode
 */
@Injectable()
export class LocalSpacingsLoaderService implements SelectOptionLoaderI {
  constructor(
    protected store: Store<OrderBuilderState>,
    protected featureToggle: FeatureToggleService,
  ) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup, config: FieldConfig): Observable<SelectOptionI[]> {
    const toggle = this.featureToggle.isFeatureEnabled(Features.EnableSpacingValidationByQtyLoadSize);
    return toggle ? this.loadOptionsByQuantityLoadSize(formGroup, config) : this.loadOptionsDefault(formGroup);
  }

  protected loadOptionsDefault(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getLocalSpacings),
      filter((state) => !!state),
      map((spacings) => {
        if (spacings.length) {
          return spacings.map((spacing) => {
            return {
              id: spacing.spacingId,
              name: `${spacing.spacingDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }

  /**
   * This method validate if the quantity is greater than the load size then show all spacing options
   * else show only the NA option
   * @param formGroup form to get fields of quantity, load size and spacing
   * @param config field config to get the name of the spacing field
   * @returns Observable of the spacings
   */
  protected loadOptionsByQuantityLoadSize(
    formGroup: UntypedFormGroup,
    config: FieldConfig,
  ): Observable<SelectOptionI[]> {
    const configName = config.name;
    const quantityCtrl = formGroup.get(SpecFieldNames.QUANTITY);
    const loadSizeCtrl = formGroup.get(SpecFieldNames.LOAD_SIZE);
    const spacingCtrl = formGroup.get(configName);

    const quantity$ = quantityCtrl.valueChanges.pipe(startWith(quantityCtrl.value));
    const loadSize$ = loadSizeCtrl.valueChanges.pipe(startWith(loadSizeCtrl.value));
    const spacings$ = this.store.pipe(select(getLocalSpacings));
    return combineLatest([spacings$, quantity$, loadSize$]).pipe(
      filter(([spacings, quantity, loadSize]) => !!spacings && !!quantity && !!loadSize),
      distinctUntilChanged(
        ([prevSpacing, prevQuantity, prevLoadSize], [currSpacing, currQuantity, currLoadSize]) =>
          JSON.stringify(prevSpacing) === JSON.stringify(currSpacing) &&
          prevQuantity === currQuantity &&
          prevLoadSize === currLoadSize,
      ),
      map(([spacings, quantity, loadSize]) => {
        if (spacings.length) {
          const spacingNACode = NA_SPACING_CODE;
          const spacingNAOption = spacings.find((spacing) => spacing.spacingCode === spacingNACode);
          const loadSizeValue = getLoadSizeValue(loadSize);
          const moreThanOneSpacing = quantity > loadSizeValue;

          if (moreThanOneSpacing) {
            if (spacingCtrl.value === spacingNAOption.spacingId) {
              spacingCtrl.reset();
              spacingCtrl.markAsDirty();
              spacingCtrl.markAsTouched();
            }
            spacingCtrl.enable();
            return spacings
              .filter((spacing) => spacing.spacingCode !== spacingNAOption.spacingCode)
              .map((spacing) => {
                return {
                  id: spacing.spacingId,
                  name: `${spacing.spacingDesc}`,
                };
              });
          }

          spacingCtrl.setValue(spacingNAOption.spacingId);
          return [
            {
              id: spacingNAOption.spacingId,
              name: `${spacingNAOption.spacingDesc}`,
            },
          ];
        }
        return [];
      }),
    );
  }
}
/**
 * Service for loading spacing options, either by
 * default or based on quantity and load size.
 * Determines whether to apply a specific validation on
 * spacings depending on feature toggles.
 */
@Injectable()
export class LocalSpacingsRepeatLoaderService implements SelectOptionLoaderI {
  constructor(
    protected store: Store<OrderBuilderState>,
    protected featureToggle: FeatureToggleService,
  ) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup, config: FieldConfig): Observable<SelectOptionI[]> {
    const toggle = this.featureToggle.isFeatureEnabled(Features.EnableSpacingValidationByQtyLoadSize);
    if (toggle) {
      return this.loadOptionsByQuantityLoadSize(formGroup, config);
    } else {
      return this.loadOptionsDefault(formGroup);
    }
  }

  /**
   * This method retrieves a list of spacing options to display a
   * selection for the user, based on data obtained from the summary.
   *
   * @param formGroup - The form group to which the options might relate.
   * @returns An observable of an array of `SelectOptionI` items representing
   * the spacing options.
   */
  protected loadOptionsDefault(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getLocalSpacings),
      filter((state) => !!state),
      map((spacings) => {
        if (spacings.length) {
          return this.mapSpacings(spacings);
        }
        return [];
      }),
      catchError((error) => {
        console.error('Error loading options:', error);
        return of([]);
      }),
    );
  }

  /**
   * Maps an array of spacing objects to an array of SelectOptionI objects.
   * This transformation is used to prepare the data for selection options
   * in the user interface.
   *
   * @param spacings - An array of spacing objects to be mapped.
   * @returns An array of SelectOptionI objects containing the id and name
   *  derived from the spacing objects.
   */
  private mapSpacings(spacings: ImSpacing[]): SelectOptionI[] {
    return spacings.map((spacing) => ({
      id: spacing.spacingCode,
      name: `${spacing.spacingDesc}`,
    }));
  }

  /**
   * This method validate if the quantity is greater than the load size then show all spacing options
   * else show only the NA option
   * @param formGroup form to get fields of quantity, load size and spacing
   * @param config field config to get the name of the spacing field
   * @returns Observable of the spacings
   */
  protected loadOptionsByQuantityLoadSize(
    formGroup: UntypedFormGroup,
    config: FieldConfig,
  ): Observable<SelectOptionI[]> {
    const configName = config.name;
    const quantityCtrl = formGroup.get(SpecFieldNames.QUANTITY);
    const loadSizeCtrl = formGroup.get(SpecFieldNames.LOAD_SIZE);
    const spacingCtrl = formGroup.get(configName);

    const quantity$ = quantityCtrl.valueChanges.pipe(startWith(quantityCtrl.value));
    const loadSize$ = loadSizeCtrl.valueChanges.pipe(startWith(loadSizeCtrl.value));
    const spacings$ = this.store.pipe(select(getLocalSpacings));
    return combineLatest([spacings$, quantity$, loadSize$]).pipe(
      filter(([spacings, quantity, loadSize]) => !!spacings && !!quantity && !!loadSize),
      distinctUntilChanged(
        ([prevSpacing, prevQuantity, prevLoadSize], [currSpacing, currQuantity, currLoadSize]) =>
          JSON.stringify(prevSpacing) === JSON.stringify(currSpacing) &&
          prevQuantity === currQuantity &&
          prevLoadSize === currLoadSize,
      ),
      map(([spacings, quantity, loadSize]) => {
        if (spacings.length) {
          const spacingNACode = NA_SPACING_CODE;
          const spacingNAOption = spacings.find((spacing) => spacing.spacingCode === spacingNACode);
          const loadSizeValue = getLoadSizeValue(loadSize);
          const moreThanOneSpacing = quantity > loadSizeValue;

          if (moreThanOneSpacing) {
            if (spacingCtrl.value === spacingNAOption.spacingId) {
              spacingCtrl.reset();
              spacingCtrl.markAsDirty();
            }
            spacingCtrl.enable();
            return spacings
              .filter((spacing) => spacing.spacingCode !== spacingNAOption.spacingCode)
              .map((spacing) => {
                return {
                  id: spacing.spacingId,
                  name: `${spacing.spacingDesc}`,
                };
              });
          }

          spacingCtrl.setValue(spacingNAOption.spacingId);
          return [
            {
              id: spacingNAOption.spacingId,
              name: `${spacingNAOption.spacingDesc}`,
            },
          ];
        }
        return [];
      }),
      catchError((error) => {
        console.error('Error loading options:', error);
        return of([]);
      }),
    );
  }
}

@Injectable()
export class LocalSpacingsLoaderModifyService extends LocalSpacingsLoaderService {
  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup, config: FieldConfig): Observable<SelectOptionI[]> {
    const toggle = this.featureToggle.isFeatureEnabled(Features.EnableSpacingValidationByQtyLoadSize);
    const quantity = formGroup.get(SpecFieldNames.QUANTITY).value;
    const loadSizeValue = getLoadSizeValue(formGroup.get(SpecFieldNames.LOAD_SIZE).value);
    const spacingForm = formGroup.get(SpecFieldNames.SPACING).value;
    const spacings$ = this.store.select(getLocalSpacings);
    if (!toggle) {
      return this.loadOptionsDefault(formGroup);
    } else {
      return combineLatest([this.store.select(getDcoStatusState), spacings$]).pipe(
        filter(([dcoState, spacings]) => !!spacings),
        switchMap(([dcoState, spacings]) => {
          const moreThanOneSpacing = quantity > loadSizeValue;
          const spacingSelected = spacings.find((spacing) => spacing.spacingId === spacingForm);
          // Validate whether the order to be modified follows the feature rules. If not, display all spacings
          if ((spacingSelected == null && formGroup.invalid) || !dcoState) {
            return this.loadOptionsByQuantityLoadSize(formGroup, config);
          }
          if (
            !!spacingSelected &&
            (dcoState.haveChanges ||
              (spacingSelected.spacingCode === NA_SPACING_CODE && !moreThanOneSpacing) ||
              (spacingSelected.spacingCode !== NA_SPACING_CODE && moreThanOneSpacing))
          ) {
            return this.loadOptionsByQuantityLoadSize(formGroup, config);
          }
          return this.loadOptionsDefault(formGroup);
        }),
      );
    }
  }
}

@Injectable()
export class SingleUnloadTimesLoaderService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getUnloadTimes),
      filter((state) => !!state),
      map((unloadTimes) => {
        if (unloadTimes.length) {
          return unloadTimes.map((unloadTime) => {
            return {
              id: unloadTime.unloadTimeCode,
              name: `${unloadTime.unloadTimeDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}

@Injectable()
export class SingleUnloadTimesLocalUKLoaderService implements SelectOptionLoaderI {
  constructor(
    private store: Store<OrderBuilderState>,
    private featureToggleService: FeatureToggleService,
  ) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return combineLatest([
      this.store.pipe(select(getFilteredUnloadTimes)),
      this.store.pipe(select(getUnloadTimesWithLocalData)),
      this.store.pipe(select(getOrderSelectedDetail)),
      this.store.pipe(select(getRmsSpecificationsIsModify)),
    ]).pipe(
      filter(([unloadTimes]) => !!unloadTimes),
      map(([filteredUnloadTimes, unloadTimes, selectedOrder, isModify]) => {
        if (this.featureToggleService.isFeatureEnabled(Features.UNLOAD_TYPE_AND_APPLICATION_CATALOGUES_HOMOLOGATION)) {
          const isAfterCutOff = isAfterCutOffTimeUK(_safe(() => selectedOrder.programmedDateTime));
          if (isAfterCutOff) {
            const selectedUnloadTypeValue = formGroup.get(SpecFieldNames.UNLOAD_TYPE).value;
            const storedUnloadTypeValue = _safe(
              () => selectedOrder.orderProducts[0].itemProperties.unloadType.unloadTypeCode,
            );
            const storedUnloadTimeValue = _safe(
              () => selectedOrder.orderProducts[0].itemProperties.unloadTime.unloadTimeCode,
            );
            const unloadTypeMinutes = UNLOAD_TYPE_MIN_TIMES_MAP[storedUnloadTypeValue];
            const unloadTimeMinutes = UNLOAD_TIMES_TO_UNLOAD_TYPE_MAP[storedUnloadTimeValue];

            if (selectedUnloadTypeValue !== storedUnloadTypeValue) {
              const maxSelectedUnloadTypeMinutes = UNLOAD_TYPE_MIN_TIMES_MAP[selectedUnloadTypeValue];
              return unloadTimes
                .filter((unloadTime) => unloadTime.unloadTimeMinutes <= maxSelectedUnloadTypeMinutes)
                .map((unloadTime) => ({
                  id: unloadTime.unloadTimeCode,
                  name: `${unloadTime.unloadTimeDesc}`,
                }));
            }

            if (unloadTimeMinutes < unloadTypeMinutes) {
              return unloadTimes
                .filter((time) => time.unloadTimeMinutes <= unloadTimeMinutes)
                .map((time) => ({
                  id: time.unloadTimeCode,
                  name: `${time.unloadTimeDesc}`,
                }));
            }

            return unloadTimes
              .filter(
                (time) => time.unloadTimeMinutes <= unloadTimeMinutes && time.unloadTimeMinutes >= unloadTypeMinutes,
              )
              .map((time) => ({
                id: time.unloadTimeCode,
                name: `${time.unloadTimeDesc}`,
              }));
          }

          if (filteredUnloadTimes.length) {
            return filteredUnloadTimes.map((unloadTime) => ({
              id: unloadTime.unloadTimeCode,
              name: `${unloadTime.unloadTimeDesc}`,
            }));
          }
          return [];
        }

        // Get the selected unload time minutes from the original order
        const selectedUnloadTimeMinutes = _safe(
          () => selectedOrder.orderProducts[0].itemProperties.unloadTime['unloadTimeMinutes'],
        );
        // this variable is to decide if we should apply the new filter for <= unload time
        const shouldApplyFilter =
          this.featureToggleService.isFeatureEnabled(Features.IMPROVEMENTS_MODIFY_ORDER_RULES) &&
          isAfterCutOffTimeUK(_safe(() => selectedOrder.programmedDateTime)) &&
          isModify;

        const unloadTimesFilter = unloadTimes.filter(
          (unloadTime, index, self) =>
            index === self.findIndex((o) => o.unloadTimeId === unloadTime.unloadTimeId) &&
            // for new modification rules after cut off time we need only unload times that are equal or less than
            // the original order unload time
            (!shouldApplyFilter || unloadTime['unloadTimeMinutes'] <= selectedUnloadTimeMinutes),
        );

        if (unloadTimesFilter.length) {
          return unloadTimesFilter.map((unloadTime) => {
            const unloadTimeDesc =
              unloadTime.unloadTimeDesc.includes('min') || unloadTime.unloadTimeDesc.includes('hours')
                ? unloadTime.unloadTimeDesc
                : unloadTime['unloadTimeMinutes'] + ' min';
            return {
              id: unloadTime.unloadTimeCode,
              name: unloadTimeDesc,
            };
          });
        }
        return [];
      }),
    );
  }
}

@Injectable()
export class SingleUnloadTimesLocalMXLoaderService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getUnloadTimesWithLocalData),
      filter((state) => !!state),
      map((unloadTimes) => {
        const unloadTimesFilter = unloadTimes.filter(
          (unloadTime, index, self) => index === self.findIndex((o) => o.unloadTimeId === unloadTime.unloadTimeId),
        );
        if (unloadTimesFilter.length) {
          // Uncomment to sort by minutes if needed
          // const sorted = unloadTimesFilter.sort((a, b) => (a['unloadTimeMinutes'] - b['unloadTimeMinutes']));
          return unloadTimesFilter.map((unloadTime) => {
            const unloadTimeDesc =
              unloadTime.unloadTimeDesc.includes('min') || unloadTime.unloadTimeDesc.includes('hours')
                ? unloadTime.unloadTimeDesc
                : unloadTime['unloadTimeMinutes'] + ' min';
            return {
              id: unloadTime.unloadTimeCode,
              name: unloadTimeDesc,
            };
          });
        }
        return [];
      }),
    );
  }
}

@Injectable()
export class FilteredUnloadTimesLoaderService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}
  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getFilteredUnloadTimes),
      filter((state) => !!state),
      map((unloadTimes) => {
        if (unloadTimes.length) {
          return unloadTimes.map((unloadTime) => {
            return {
              id: unloadTime.unloadTimeCode,
              name: `${unloadTime.unloadTimeDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}

@Injectable()
export class SingleUnloadTypesLoaderService implements SelectOptionLoaderI {
  constructor(
    protected store: Store<OrderBuilderState>,
    private router: Router,
  ) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getUnloadTypes),
      filter((state) => !!state),
      map((unloadTypes) => {
        const isRepeat = this.router.url.includes('repeat');
        const isModify = this.router.url.includes('modify');
        const unloadTypeFormValue = formGroup.get(SpecFieldNames.UNLOAD_TYPE).value;
        if (unloadTypes.length) {
          return unloadTypes.map((unloadType) => {
            return {
              id: unloadType.unloadTypeCode,
              name: `${unloadType.unloadTypeDesc}`,
            };
          });
        }
        if (!unloadTypeFormValue && (isRepeat || isModify)) {
          formGroup.get(SpecFieldNames.UNLOAD_TYPE).setErrors({ required: true });
          formGroup.get(SpecFieldNames.UNLOAD_TYPE).markAsTouched();
        }
        return [];
      }),
    );
  }
}

@Injectable()
export class FilteredUnloadTypesLoaderService implements SelectOptionLoaderI {
  constructor(protected store: Store<OrderBuilderState>) {}
  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      // FIRST DATA LOAD UPDATES FULL CATALOG AND WOULDN'T BE CATCHED BY PREVIOUS SELECTOR
      select(getFullAndFilterUnloadTypes),
      filter((state) => !!state),
      map((unloadTypes) => {
        if (unloadTypes.filtered.length) {
          return unloadTypes.filtered.map((unloadType) => {
            return {
              id: unloadType.unloadTypeCode,
              name: `${unloadType.unloadTypeDesc}`,
            };
          });
        } else if (unloadTypes.full.length) {
          return unloadTypes.full.map((unloadType) => {
            return {
              id: unloadType.unloadTypeCode,
              name: `${unloadType.unloadTypeDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}

@Injectable()
export class SingleSlumpsLoaderService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getSlumps),
      filter((state) => !!state),
      map((slumps) => {
        if (slumps.length) {
          if (slumps.length === 1) {
            formGroup.patchValue(
              {
                [SpecFieldNames.SLUMP]: slumps[0].slumpCode,
              },
              { emitEvent: false },
            );
          }
          return slumps.map((slump) => {
            return {
              id: slump.slumpCode,
              name: `${slump.slumpDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}

@Injectable()
export class SingleSlumpsTechProdLoaderService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getSlumpsAndProducts),
      filter((state) => !!state.slumps),
      map((state) => {
        if (!!state.slumps.length) {
          return state.slumps.map((slump) => {
            return {
              id: slump.slumpCode,
              name: `${slump.slumpDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}

@Injectable()
export class SingleSlumpProductLoaderService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getData),
      filter(({ stateProducts, stateSlumps, experienceCode, recipes }) => !!_safe(() => stateProducts.length)),
      map((state) => {
        let slumps: any;
        const code = formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value;
        const stateRecipe = _safe(() => state.recipes.find((recipe) => recipe.id === formGroup.get('id').value));

        if (state.experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK) {
          slumps = !!code && !!state.stateSlumps && state.stateSlumps[code];
        } else {
          const techProd = _safe(
            () =>
              state.stateProducts.find((item) => item.productCode.includes(code)).technicalProducts[0]
                .technicalProductCode,
          );
          slumps = !!techProd && !!state.stateSlumps && state.stateSlumps[techProd];
        }

        const defaultValue =
          _safe(() => slumps.slumpProduct.targetSlump) > 0
            ? _safe(() => slumps.slumpProduct.targetSlump.toString())
            : _safe(() => slumps.slumpProduct.slumpLow.toString());

        if (stateRecipe.values.productSelect !== code) {
          formGroup.get(SpecFieldNames.SLUMP_PRODUCT).setValue(defaultValue);
        }

        if (_safe(() => slumps.slumps.length)) {
          return slumps.slumps.map((item) => {
            return {
              id: item.slumpCode,
              name: `${item.slumpDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}
@Injectable()
export class SingleSlumpProductLoaderCommandUlService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getDataModify),
      filter(({ stateProducts, stateSlumps, experienceCode, recipes }) => !!_safe(() => stateProducts.length)),
      map((state) => {
        let slumps: any;
        const code = formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value;
        const stateRecipe = _safe(() => state.recipes.find((recipe) => recipe.id === formGroup.get('id').value));
        if (state.experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK) {
          slumps = !!code && !!state.stateSlumps && state.stateSlumps[code];
        } else {
          const techProd = _safe(
            () =>
              state.stateProducts.find((item) => item.productCode.includes(code)).technicalProducts[0]
                .technicalProductCode,
          );
          slumps = !!techProd && !!state.stateSlumps && state.stateSlumps[techProd];
        }

        const defaultValue =
          _safe(() => slumps.slumpProduct.targetSlump) > 0
            ? _safe(() => slumps.slumpProduct.targetSlump.toString())
            : _safe(() => slumps.slumpProduct.slumpLow.toString());

        if (stateRecipe.values.productSelect !== code) {
          formGroup.get(SpecFieldNames.SLUMP_PRODUCT).setValue(defaultValue);
        }

        if (_safe(() => slumps.slumps.length)) {
          return slumps.slumps.map((item) => {
            return {
              id: item.slumpCode,
              name: `${item.slumpDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}

@Injectable()
export class SingleSlumpMultipleProductLoaderService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getDataForm),
      filter(({ updateForm, stateProducts, stateSlumps, experienceCode }) => !!_safe(() => stateProducts.length)),
      filter(
        ({ updateForm, stateProducts, stateSlumps, experienceCode }) =>
          !!_safe(
            () =>
              updateForm &&
              updateForm.id === formGroup.get(SpecFieldNames.ID).value &&
              _safe(() => updateForm.values.productSelect) !== formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value,
          ) || updateForm === null,
      ),
      distinctUntilChanged(),
      map((state) => {
        let slumps: any;

        const code = formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value;
        if (state.experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK) {
          slumps = !!code && !!state.stateSlumps && state.stateSlumps[code];
        } else {
          const techProd = _safe(
            () =>
              state.stateProducts.find((item) => item.productCode.includes(code)).technicalProducts[0]
                .technicalProductCode,
          );
          slumps = !!techProd && !!state.stateSlumps && state.stateSlumps[techProd];
        }

        const defaultValue =
          _safe(() => slumps.slumpProduct.targetSlump) > 0
            ? _safe(() => slumps.slumpProduct.targetSlump.toString())
            : _safe(() => slumps.slumpProduct.slumpLow.toString());
        if (
          state.updateForm !== null &&
          !!state.updateForm.values.productSelect &&
          code !== state.updateForm.values.productSelect
        ) {
          formGroup.get(SpecFieldNames.SLUMP_PRODUCT).setValue(defaultValue);
        }

        if (_safe(() => slumps.slumps.length)) {
          return slumps.slumps.map((item) => {
            return {
              id: item.slumpCode,
              name: `${item.slumpDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}
@Injectable()
export class SlumpProductRepeatLoaderService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getRepeatData),
      filter(({ stateProducts, stateSlumps, experienceCode, recipes }) => !!_safe(() => stateProducts.length)),
      map(({ stateProducts, stateSlumps, experienceCode, recipes }) => {
        let slumps: any;
        const productCode = formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value;

        if (experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK) {
          slumps = !!productCode && !!stateSlumps && stateSlumps[productCode];
        } else {
          const techProd = _safe(
            () =>
              stateProducts.find((item) => item.productCode.includes(productCode)).technicalProducts[0]
                .technicalProductCode,
          );
          slumps = !!techProd && !!stateSlumps && stateSlumps[techProd];
        }

        const defaultValue =
          _safe(() => slumps.slumpProduct.targetSlump) > 0
            ? _safe(() => slumps.slumpProduct.targetSlump.toString())
            : _safe(() => slumps.slumpProduct.slumpLow.toString());
        const recipe = _safe(() => recipes.find((item) => item.id === formGroup.get('id').value));

        if (productCode !== recipe.values.productSelect) {
          formGroup.get(SpecFieldNames.SLUMP_PRODUCT).setValue(defaultValue);
        }

        if (_safe(() => slumps.slumps.length)) {
          return slumps.slumps.map((item) => {
            return {
              id: item.slumpCode,
              name: `${item.slumpDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}

@Injectable()
export class SingleSlumpProductLegacyLoaderService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getDataLegacy),
      filter(({ stateProducts, stateSlumps, experienceCode, recipes }) => !!_safe(() => stateProducts.length)),
      map((state) => {
        let slumps: any;
        const code = formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value;
        const stateRecipe = _safe(() => state.recipes.find((recipe) => recipe.id === formGroup.get('id').value));
        if (state.experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK) {
          slumps = !!code && !!state.stateSlumps && state.stateSlumps[code];
        } else {
          const techProd = _safe(
            () =>
              state.stateProducts.find((item) => item.comm_product.comm_product.includes(code)).comm_product
                .productRecommendation[0].technicalProduct.tech_prod,
          );
          slumps = !!techProd && state.stateSlumps && state.stateSlumps[techProd];
        }

        const defaultValue =
          _safe(() => slumps.slumpProduct.targetSlump) > 0
            ? _safe(() => slumps.slumpProduct.targetSlump.toString())
            : _safe(() => slumps.slumpProduct.slumpLow.toString());

        if (stateRecipe.values.productSelect !== code) {
          formGroup.get(SpecFieldNames.SLUMP_PRODUCT).setValue(defaultValue);
        }

        if (_safe(() => slumps.slumps.length)) {
          return slumps.slumps.map((item) => {
            return {
              id: item.slumpCode,
              name: `${item.slumpDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}

@Injectable()
export class SingleLoadSizesLoaderService implements SelectOptionLoaderI {
  protected defaultLoadSize = false;
  constructor(
    private store: Store<OrderBuilderState>,
    private experienceService: ExperiencesService,
  ) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   *
   * defaultLoadSize is set to false by default
   * defaultLoadSize is only apply in order creation, not in order modification and repeat
   * In modify and repeat, the default load size is set in the form by the summary info
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getLoadSizes),
      filter((state) => !!state),
      map((loadSizes) => {
        if (loadSizes.length) {
          if (this.defaultLoadSize) {
            const experiences = this.experienceService.commandDcoEnabled();
            experiences && formGroup.get(SpecFieldNames.LOAD_SIZE).setValue('10yd3');
          }
          return loadSizes.map((loadSize) => {
            return {
              id: loadSize.loadSizeCode,
              name: `${loadSize.loadSizeDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}

@Injectable()
export class SingleLoadSizesLoaderDefaultLoadSizeService extends SingleLoadSizesLoaderService {
  defaultLoadSize = true;
}

/**
 * Retrieves filtered list of pumpabilities (for FR)
 */
@Injectable()
export class SinglePumpabilityFilteredLoaderService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getFilteredPumpabilities),
      filter((pumpability) => !!pumpability && pumpability.length > 0),
      map((pumpability) => {
        if (!!formGroup.getRawValue()[SpecFieldNames.PRODUCT_SELECTION]) {
          if (pumpability.length === 1) {
            formGroup.patchValue({ [SpecFieldNames.PUMPABILITY]: pumpability[0].id }, { emitEvent: true });
          }
          return pumpability.map((pump) => {
            return {
              id: pump.id,
              name: `${pump.name}`,
            };
          });
        }
        return [];
      }),
    );
  }
}

@Injectable()
export class SinglePumpcapacitiesLoaderService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getPumpCapacities),
      filter((state) => !!state),
      map((pumpCapacitiesState) => {
        if (pumpCapacitiesState.length) {
          return pumpCapacitiesState.map((pumpCapacities) => {
            return {
              id: pumpCapacities.pumpCapacityCode,
              name: `${pumpCapacities.pumpCapacityDesc}`,
            };
          });
        }
        return [];
      }),
    );
  }
}

/**
 * Used only in FR create
 */
@Injectable()
export class UnloadTypeLoaderService extends FilteredUnloadTypesLoaderService {
  constructor(protected store: Store<OrderBuilderState>) {
    super(store);
  }
  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return combineLatest([
      super.loadOptions(formGroup),
      this.store.select(getOpenPlatformProductAddedValues('undefined_0')).pipe(
        map((availables) => availables),
        filter((availables) => !!availables && availables.every((av) => !av.loading)),
        map((availables) => availables[availables.length - 1]),
        map((last) => last && last.technicalProduct),
        map((technicals) => {
          if (technicals) {
            if (technicals.length === 0) {
              return true;
            } else {
              return technicals.some((technical) => technical.isPumpable);
            }
          } else {
            return true;
          }
        }),
        catchError(() => of(true)),
      ),
    ]).pipe(
      map(([options, pumpable]) => {
        const pumpCodes: string[] = Object.keys(UnloadTypePumpCodesFrance).map(
          (code) => UnloadTypePumpCodesFrance[code],
        );
        const pumpabilityValue = _safe(() => formGroup.get(SpecFieldNames.PUMPABILITY).value.toString());
        const notPumpableOptions = options.filter((option) => !pumpCodes.includes(option.id));
        if (!pumpabilityValue) {
          return pumpable ? options : notPumpableOptions;
        }
        return pumpabilityValue === '0' || !pumpable ? notPumpableOptions : options;
      }),
    );
  }
}

@Injectable()
export class UnloadTypeLoaderRepeatService {
  constructor(private store: Store<OrderBuilderState>) {}
  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.select(getVAPAndFilteredUnloadTypes('undefined_0')).pipe(
      debounceTime(600),
      filter(
        (state) =>
          !!state.unloadTypes &&
          state.unloadTypes.length > 0 &&
          _safe(() => formGroup.get(SpecFieldNames.PUMPABILITY).value) !== undefined,
      ),
      map((state) => {
        const technicals = _safe(() => state.availables[state.availables.length - 1].technicalProduct);
        let pumpable = true;
        if (technicals) {
          if (technicals.length === 0) {
            pumpable = true;
          } else {
            pumpable = technicals.some((technical) => technical.isPumpable);
          }
        } else {
          pumpable = true;
        }

        const options = state.unloadTypes.map((unloadType) => {
          return {
            id: unloadType.unloadTypeCode,
            name: `${unloadType.unloadTypeDesc}`,
          };
        });

        const pumpCodes: string[] = Object.keys(UnloadTypePumpCodesFrance).map(
          (code) => UnloadTypePumpCodesFrance[code],
        );
        const pumpabilityValue = _safe(() => formGroup.get(SpecFieldNames.PUMPABILITY).value.toString());
        const notPumpableOptions = options.filter((option) => !pumpCodes.includes(option.id));
        if (!pumpabilityValue) {
          return pumpable ? options : notPumpableOptions;
        }
        return pumpabilityValue === '0' || !pumpable ? notPumpableOptions : options;
      }),
    );
  }
}
